/**
 * Starts a re-grade of a finished run, in a modal: a new run with the same outputs,
 * graded again. The model under test is not called, so only the judge changes. The
 * modal says that, shows which judge prompt versions are compared, and estimates the cost
 * from this run's own judge calls.
 */
import { RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import { Modal } from '../../../../components/Modal';
import { evalsService } from '../../services/evalsService';
import type { EvalModel, EvalRunDetail, EvalSuiteInfo, RegradeScope } from '../../types/evalTypes';
import { COST_NOTE, formatUsd, sumCosts, trialCost } from '../../utils/evalCost';
import { JudgeVersionSelect, withJudgeVersion } from '../JudgeVersionSelect';

/** Re-grade trials run 10 at a time on the API (evalRunJob.ts). */
const CONCURRENCY = 10;

interface RegradeFormProps {
  detail: EvalRunDetail;
  suite: EvalSuiteInfo | undefined;
  models: EvalModel[];
  onClose: () => void;
}

export const RegradeForm = ({ detail, suite, models, onClose }: RegradeFormProps) => {
  const navigate = useNavigate();
  const graded = detail.results.filter((result) => result.status === 'ok' && result.output);
  const flagged = graded.filter((result) => result.judgeFlag !== null);

  const [scope, setScope] = useState<RegradeScope>(flagged.length > 0 ? 'flagged' : 'all');
  const [judgeVersions, setJudgeVersions] = useState<Record<string, string>>({});
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = scope === 'flagged' ? flagged : graded;
  // The same outputs, so the judge reads about as many tokens as it did in this run.
  const cost = sumCosts(selected.map((result) => trialCost(models, result).judge));
  const waves = Math.ceil(selected.length / CONCURRENCY);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const { runId } = await evalsService.startRegrade(detail.run.runId, scope, judgeVersions);
      // Closed first: the new run opens on the same page component, which keeps its state.
      onClose();
      navigate(`/evals/${runId}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to start the re-grade.');
      setStarting(false);
    }
  };

  const option = (value: RegradeScope, label: string, hint: string, total: number) => (
    <label className={`flex items-start gap-2 rounded-lg border p-2 text-sm ${scope === value ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
      <input type="radio" name="regrade-scope" className="mt-1" checked={scope === value} disabled={starting || total === 0} onChange={() => setScope(value)} />
      <span>
        <span className="font-medium text-gray-900">
          {label} ({total})
        </span>
        <span className="block text-xs text-gray-500">{hint}</span>
      </span>
    </label>
  );

  return (
    <Modal isOpen onClose={onClose} title="Re-grade with a judge prompt" size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
          This makes a <strong>new run</strong> with the same outputs as this one. Only the judge runs; the model under test is
          not called. This run does not change. The new run shows a report against this one.
        </p>

        <div className="space-y-2">
          {option('flagged', 'Results with a judge flag', 'The human flags are the labels: the report shows which ones the judge now gets right.', flagged.length)}
          {option('all', 'Every result with an output', 'Also shows checks nobody flagged whose verdict changed: possible regressions.', graded.length)}
        </div>

        {suite?.judgePrompts.map((judge) => {
          const before = detail.run.judgePrompts.find((ref) => ref.judgeName === judge.judgeName);
          const afterId = judgeVersions[judge.judgeName] ?? judge.currentVersionId;
          const after = judge.versions.find((version) => version.versionId === afterId);
          return (
            <div key={judge.judgeName} className="space-y-1">
              <JudgeVersionSelect
                judge={judge}
                value={judgeVersions[judge.judgeName] ?? ''}
                disabled={starting}
                onChange={(versionId) => setJudgeVersions((previous) => withJudgeVersion(previous, judge.judgeName, versionId))}
              />
              <p className={`text-xs ${before && after && before.versionId === after.versionId ? 'text-amber-700' : 'text-gray-600'}`}>
                {before ? `v${before.number} (this run)` : 'Unknown version (this run)'} → {after ? `v${after.number}` : 'none'}
                {before && after && before.versionId === after.versionId && ': the same version, so expect the same verdicts, apart from judge randomness.'}
              </p>
            </div>
          );
        })}

        <p className="text-sm text-gray-600" title={COST_NOTE}>
          {selected.length} judge calls, about {formatUsd(cost)}, in {waves} {waves === 1 ? 'wave' : 'waves'} of up to {CONCURRENCY}.
        </p>

        {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={starting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" icon={<RotateCcw size={13} />} onClick={() => void start()} disabled={starting || selected.length === 0} loading={starting}>
            Start re-grade
          </Button>
        </div>
      </div>
    </Modal>
  );
};
